import { useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';
const FILE_KEYS = ['7111', '7211', '7470', 'osv'] as const;
const OPTIONAL_FILE_KEYS = ['sole_7211', 'sole_7111', 'sole_7112', 'sole_7212', 'report_income'] as const;
const ALL_FILE_KEYS = [...FILE_KEYS, ...OPTIONAL_FILE_KEYS];
type FilesStatus = Record<string, string | null>;

const FILE_LABELS: Record<string, string> = {
  '7111': 'Счёт 7111',
  '7211': 'Счёт 7211',
  '7470': 'Счёт 7470',
  osv: 'ОСВ (Total)',
  sole_7211: 'ИП · 7211',
  sole_7111: 'ИП · 7111',
  sole_7112: 'ИП · 7112',
  sole_7212: 'ИП · 7212',
  report_income: 'Отчёт о совокупном доходе',
};

type Group = { id: string; name: string; color?: string | null; clusters: string[] };
type Section = { id: string; name: string; color?: string | null; clusters: string[] };

const UNGROUPED_KEY = '__ungrouped__';
const ALL_KEY = '__all__';
// Below this width the two columns stack, so the file panel ends up under the cluster list.
const STACKED_QUERY = '(max-width: 960px)';

export default function Upload({ apiKey }: { apiKey: string }) {
  const [clusters, setClusters] = useState<string[]>([]);
  const [selectedCluster, setSelectedCluster] = useState<string | null>(null);
  const [configs, setConfigs] = useState<string[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [groupFilter, setGroupFilter] = useState<string>('');  // '' = all
  const [search, setSearch] = useState('');
  const [defaultCluster, setDefaultCluster] = useState<string>(localStorage.getItem('defaultCluster') || 'default');
  const [defaultConfig, setDefaultConfig] = useState<string>(localStorage.getItem('defaultConfig') || 'dd');
  const [filesOnServer, setFilesOnServer] = useState<FilesStatus | null>(null);
  const panelRef = useRef<HTMLElement>(null);

  const toggleDefaultCluster = (name: string) => {
    const newDefault = defaultCluster === name ? '' : name;
    setDefaultCluster(newDefault);
    localStorage.setItem('defaultCluster', newDefault);
  };

  const toggleDefaultConfig = (name: string) => {
    const newDefault = defaultConfig === name ? '' : name;
    setDefaultConfig(newDefault);
    localStorage.setItem('defaultConfig', newDefault);
  };
  const [files, setFiles] = useState<Record<string, File | null>>({
    '7111': null, '7211': null, '7470': null, osv: null, sole_7211: null, sole_7111: null, sole_7112: null, sole_7212: null, report_income: null,
  });
  // Two status lines: file actions report inside the file panel, settings actions next to the
  // settings cards — a single page-level line showed "Курс сохранён" under the file list.
  const [status, setStatus] = useState('');
  const [settingsStatus, setSettingsStatus] = useState('');
  const [uploading, setUploading] = useState(false);
  const [newClusterName, setNewClusterName] = useState('');
  const [showOptional, setShowOptional] = useState(false);
  const [showNewCluster, setShowNewCluster] = useState(false);
  const [currencyRates, setCurrencyRates] = useState<Record<string, number>>({});
  const [currencyInput, setCurrencyInput] = useState<string>('');
  const [masterUploaded, setMasterUploaded] = useState(false);
  const [masterFile, setMasterFile] = useState<File | null>(null);
  const [masterUploading, setMasterUploading] = useState(false);

  const authHeaders = { 'X-API-Key': apiKey };

  const fetchClusters = async () => {
    try {
      const res = await fetch(`${API_URL}/clusters`, { headers: authHeaders });
      const data = await res.json();
      setClusters(data.clusters);
      if (!selectedCluster && data.clusters.length > 0) {
        setSelectedCluster(data.clusters.includes(defaultCluster) ? defaultCluster : data.clusters[0]);
      }
    } catch {
      setStatus('Нет связи с сервером.');
    }
  };

  const fetchGroups = async () => {
    try {
      const res = await fetch(`${API_URL}/groups`, { headers: authHeaders });
      const data = await res.json();
      setGroups(data.groups || []);
    } catch {}
  };

  const fetchConfigs = async () => {
    try {
      const res = await fetch(`${API_URL}/configs`, { headers: authHeaders });
      const data = await res.json();
      setConfigs(data.configs);
    } catch {}
  };

  const checkFiles = async (cluster: string) => {
    try {
      const res = await fetch(`${API_URL}/files/status?cluster=${encodeURIComponent(cluster)}`, { headers: authHeaders });
      const data = await res.json();
      setFilesOnServer(data.files);
    } catch {
      setStatus('Нет связи с сервером.');
    }
  };

  const fetchCurrency = async () => {
    try {
      const res = await fetch(`${API_URL}/currency`, { headers: authHeaders });
      const data = await res.json();
      setCurrencyRates(data.rates || {});
      if (data.rates?.kkg) setCurrencyInput(String(data.rates.kkg));
    } catch {}
  };

  const saveCurrencyRate = async () => {
    const rate = parseFloat(currencyInput);
    if (isNaN(rate) || rate <= 0) return;
    const newRates = { ...currencyRates, kkg: rate };
    try {
      await fetch(`${API_URL}/currency`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(newRates),
      });
      setCurrencyRates(newRates);
      setSettingsStatus('Курс сохранён.');
    } catch {
      setSettingsStatus('Не удалось сохранить курс.');
    }
  };

  const fetchMasterStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/master/status`, { headers: authHeaders });
      const data = await res.json();
      setMasterUploaded(data.uploaded);
    } catch {}
  };

  const uploadMaster = async () => {
    if (!masterFile) return;
    setMasterUploading(true);
    const formData = new FormData();
    formData.append('file', masterFile);
    try {
      const res = await fetch(`${API_URL}/master/upload`, {
        method: 'POST', body: formData, headers: authHeaders,
      });
      if (res.ok) {
        setSettingsStatus('Файл master загружен.');
        setMasterFile(null);
        setMasterUploaded(true);
      } else {
        setSettingsStatus('Не удалось загрузить master.');
      }
    } catch {
      setSettingsStatus('Не удалось загрузить master.');
    } finally {
      setMasterUploading(false);
    }
  };

  const deleteMaster = async () => {
    if (!window.confirm('Удалить master файл?')) return;
    try {
      await fetch(`${API_URL}/master`, { method: 'DELETE', headers: authHeaders });
      setMasterUploaded(false);
    } catch {}
  };

  useEffect(() => { fetchClusters(); fetchConfigs(); fetchCurrency(); fetchMasterStatus(); fetchGroups(); }, []);

  useEffect(() => {
    if (selectedCluster) {
      setFilesOnServer(null);
      setStatus('');
      setFiles({ '7111': null, '7211': null, '7470': null, osv: null, sole_7211: null, sole_7111: null, sole_7112: null, sole_7212: null, report_income: null });
      checkFiles(selectedCluster);
    }
  }, [selectedCluster]);

  const anyFileSelected = ALL_FILE_KEYS.some((k) => files[k] !== null);

  const handleUpload = async () => {
    if (!anyFileSelected || !selectedCluster) return;
    setUploading(true);
    setStatus('Загрузка...');

    const formData = new FormData();
    ALL_FILE_KEYS.forEach((key) => {
      if (files[key]) {
        formData.append(`file_${key}`, files[key]!);
      }
    });

    try {
      const res = await fetch(`${API_URL}/upload?cluster=${encodeURIComponent(selectedCluster)}`, {
        method: 'POST', body: formData, headers: authHeaders,
      });
      const data = await res.json();
      if (res.ok) {
        setStatus('Файлы успешно загружены.');
        setFiles({ '7111': null, '7211': null, '7470': null, osv: null, sole_7211: null, sole_7111: null, sole_7112: null, sole_7212: null, report_income: null });
        checkFiles(selectedCluster);
      } else {
        setStatus(`Ошибка загрузки: ${data.detail || 'неизвестная ошибка'}`);
      }
    } catch {
      setStatus('Ошибка загрузки: нет связи с сервером.');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateCluster = async () => {
    const name = newClusterName.trim();
    if (!name) return;
    try {
      const res = await fetch(`${API_URL}/clusters`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (res.ok) {
        setNewClusterName('');
        setShowNewCluster(false);
        await fetchClusters();
        setSelectedCluster(name);
      } else {
        setStatus(data.detail || 'Не удалось создать кластер');
      }
    } catch {
      setStatus('Нет связи с сервером.');
    }
  };

  const handleDeleteCluster = async (name: string) => {
    if (!window.confirm(`Удалить кластер «${name}» вместе со всеми файлами?`)) return;
    try {
      await fetch(`${API_URL}/clusters/${encodeURIComponent(name)}`, { method: 'DELETE', headers: authHeaders });
      if (selectedCluster === name) {
        setSelectedCluster(null);
        setFilesOnServer(null);
      }
      await fetchClusters();
    } catch {
      setStatus('Нет связи с сервером.');
    }
  };

  const selectCluster = (name: string) => {
    setSelectedCluster(name);
    // Stacked layout: the panel is below a long list, so bring it into view instead of making the
    // user hunt for it — the reason the page felt unresponsive on click.
    if (window.matchMedia(STACKED_QUERY).matches) {
      requestAnimationFrame(() => panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  };

  const groupsByCluster = (cluster: string): Group[] =>
    groups.filter((g) => g.clusters.includes(cluster));

  const query = search.trim().toLowerCase();
  const matchesQuery = (c: string) => !query || c.toLowerCase().includes(query);

  // Without a filter each cluster sits under its FIRST group only, and clusters in no group go to
  // "Без группы". With a group filter the chosen group is shown whole. Search narrows either view.
  const sections: Section[] = (() => {
    if (groupFilter) {
      const g = groups.find((x) => x.id === groupFilter);
      if (!g) return [];
      return [{ id: g.id, name: g.name, color: g.color, clusters: clusters.filter((c) => g.clusters.includes(c) && matchesQuery(c)) }];
    }
    if (groups.length === 0) {
      return [{ id: ALL_KEY, name: 'Все кластеры', color: null, clusters: clusters.filter(matchesQuery) }];
    }
    const assigned = new Set<string>();
    const out: Section[] = [];
    for (const g of groups) {
      const list = clusters.filter((c) => g.clusters.includes(c) && !assigned.has(c));
      list.forEach((c) => assigned.add(c));
      const shown = list.filter(matchesQuery);
      if (shown.length) out.push({ id: g.id, name: g.name, color: g.color, clusters: shown });
    }
    const orphans = clusters.filter((c) => !assigned.has(c) && matchesQuery(c));
    if (orphans.length) out.push({ id: UNGROUPED_KEY, name: 'Без группы', color: null, clusters: orphans });
    return out;
  })();

  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('uploadCollapsedSections');
      if (saved) return new Set(JSON.parse(saved) as string[]);
    } catch {}
    return new Set([UNGROUPED_KEY]);  // collapse "Без группы" by default
  });
  const toggleSection = (id: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      localStorage.setItem('uploadCollapsedSections', JSON.stringify(Array.from(next)));
      return next;
    });
  };
  // Searching or filtering by group must show the hits, whatever the saved collapse state is.
  const isSectionOpen = (id: string) => id === ALL_KEY || !!query || !!groupFilter || !collapsedSections.has(id);

  const pickFile = (key: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = (e) => {
      const f = (e.target as HTMLInputElement).files?.[0] ?? null;
      if (f) setFiles((prev) => ({ ...prev, [key]: f }));
    };
    input.click();
  };

  const renderClusterChip = (c: string, sectionId: string) => {
    // The section header already names its group; only other groups the cluster belongs to are news.
    const otherGroups = groupsByCluster(c).filter((g) => g.id !== sectionId);
    const isDefault = defaultCluster === c;
    return (
      <div
        key={c}
        className={`up-chip${selectedCluster === c ? ' is-active' : ''}`}
        onClick={() => selectCluster(c)}
        onKeyDown={(e) => { if (e.key === 'Enter') selectCluster(c); }}
        tabIndex={0}
      >
        <button
          type="button"
          className={`up-chip-default${isDefault ? ' is-on' : ''}`}
          onClick={(e) => { e.stopPropagation(); toggleDefaultCluster(c); }}
          title={isDefault ? 'Открывается по умолчанию — нажмите, чтобы снять' : 'Открывать этот кластер по умолчанию'}
          aria-pressed={isDefault}
        >
          {isDefault ? '★' : '☆'}
        </button>
        <span className="up-chip-name" title={c}>{c}</span>
        {otherGroups.map((g) => (
          <span key={g.id} className="up-badge" style={g.color ? { background: g.color } : undefined} title={`Также в группе «${g.name}»`}>
            {g.name}
          </span>
        ))}
        <button
          type="button"
          className="up-chip-delete"
          onClick={(e) => { e.stopPropagation(); handleDeleteCluster(c); }}
          title="Удалить кластер"
          aria-label={`Удалить кластер ${c}`}
        >
          &times;
        </button>
      </div>
    );
  };

  const renderFileRow = (key: string) => {
    const onServer = filesOnServer?.[key];
    const pending = files[key];
    const state = pending ? 'is-pending' : onServer ? 'is-present' : 'is-missing';
    const caption = pending ? `${pending.name} — будет загружен` : onServer ? onServer : 'не загружен';
    return (
      <div className={`up-file ${state}`} key={key}>
        <div className="up-file-meta">
          <span className="up-file-label">{FILE_LABELS[key] || key}</span>
          <span className="up-file-name" title={caption}>{caption}</span>
        </div>
        {pending ? (
          <button type="button" className="file-replace" onClick={() => setFiles((prev) => ({ ...prev, [key]: null }))}>
            Отмена
          </button>
        ) : (
          <button type="button" className="file-replace" onClick={() => pickFile(key)}>
            {onServer ? 'Заменить' : 'Выбрать'}
          </button>
        )}
      </div>
    );
  };

  const requiredPresent = FILE_KEYS.filter((k) => filesOnServer?.[k]).length;
  const optionalPresent = OPTIONAL_FILE_KEYS.filter((k) => filesOnServer?.[k]).length;
  const optionalPending = OPTIONAL_FILE_KEYS.filter((k) => files[k]).length;

  return (
    <div className="up-page">
      <div className="up-header">
        <h2>Кластеры документов</h2>
        <div className="up-toolbar">
          <input
            type="search"
            className="up-search"
            placeholder="Поиск кластера…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {groups.length > 0 && (
            <select
              className="up-select"
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              title="Показать только одну группу"
            >
              <option value="">Все группы</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name} ({g.clusters.length})</option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="up-layout">
        <div className="up-browser">
          {sections.length === 0 && (
            <div className="up-empty">{query ? `Ничего не найдено по «${search.trim()}»` : 'Кластеров пока нет'}</div>
          )}
          {sections.map((s) => {
            const open = isSectionOpen(s.id);
            return (
              <section key={s.id} className="up-section">
                {s.id !== ALL_KEY && (
                  <button
                    type="button"
                    className="up-section-head"
                    onClick={() => toggleSection(s.id)}
                    aria-expanded={open}
                  >
                    <span className="up-section-arrow">{open ? '▾' : '▸'}</span>
                    {s.color && <span className="up-section-swatch" style={{ background: s.color }} />}
                    <span>{s.name}</span>
                    <span className="up-section-count">{s.clusters.length}</span>
                  </button>
                )}
                {open && (
                  <div className="up-section-body">
                    {s.clusters.map((c) => renderClusterChip(c, s.id))}
                  </div>
                )}
              </section>
            );
          })}

          {!showNewCluster ? (
            <button type="button" className="up-add" onClick={() => setShowNewCluster(true)}>
              + Добавить кластер
            </button>
          ) : (
            <div className="up-new">
              <input
                type="text"
                className="up-search"
                placeholder="Название кластера"
                value={newClusterName}
                onChange={(e) => setNewClusterName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateCluster()}
                autoFocus
              />
              <button type="button" className="file-replace" onClick={handleCreateCluster}>Создать</button>
              <button type="button" className="file-replace" onClick={() => { setShowNewCluster(false); setNewClusterName(''); }}>Отмена</button>
            </div>
          )}
        </div>

        <aside className="up-panel" ref={panelRef}>
          {selectedCluster ? (
            <>
              <div className="up-panel-head">
                <div className="up-panel-heading">
                  <div className="up-panel-eyebrow">Файлы кластера</div>
                  <h3 className="up-panel-title">{selectedCluster}</h3>
                </div>
                {filesOnServer && (
                  <span
                    className={`up-ready${requiredPresent === FILE_KEYS.length ? ' is-ready' : ''}`}
                    title="Сколько обязательных файлов уже загружено"
                  >
                    {requiredPresent} / {FILE_KEYS.length}
                  </span>
                )}
              </div>

              {FILE_KEYS.map(renderFileRow)}

              <button
                type="button"
                className="up-optional-toggle"
                onClick={() => setShowOptional(!showOptional)}
                aria-expanded={showOptional}
              >
                {showOptional ? '▾' : '▸'} Дополнительные файлы
                <span className="up-optional-count">
                  {optionalPresent} из {OPTIONAL_FILE_KEYS.length}{optionalPending ? ` · выбрано ${optionalPending}` : ''}
                </span>
              </button>
              {showOptional && OPTIONAL_FILE_KEYS.map(renderFileRow)}

              <div className="actions">
                <button onClick={handleUpload} disabled={!anyFileSelected || uploading}>
                  {uploading ? 'Загрузка...' : 'Загрузить файлы'}
                </button>
              </div>
              {status && <p className="status">{status}</p>}
            </>
          ) : (
            <div className="up-panel-empty">
              {status || 'Выберите кластер, чтобы посмотреть и загрузить его файлы.'}
            </div>
          )}
        </aside>
      </div>

      <section className="up-settings">
        <h2>Настройки</h2>
        <div className="up-settings-grid">
          {configs.length > 0 && (
            <div className="up-card">
              <h4>Конфиг по умолчанию</h4>
              <p className="up-card-hint">Подставляется при открытии отчёта.</p>
              <div className="up-config-list">
                {configs.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`up-config${defaultConfig === c ? ' is-on' : ''}`}
                    onClick={() => toggleDefaultConfig(c)}
                    aria-pressed={defaultConfig === c}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="up-card">
            <h4>Файл Master</h4>
            <p className="up-card-hint">Используется для сравнения на странице Master.</p>
            <div className="up-inline">
              {masterUploaded && !masterFile ? (
                <>
                  <span className="file-name">загружен</span>
                  <button className="file-replace" onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.xlsx';
                    input.onchange = (e) => {
                      const f = (e.target as HTMLInputElement).files?.[0] ?? null;
                      if (f) setMasterFile(f);
                    };
                    input.click();
                  }}>Заменить</button>
                  <button className="file-replace" onClick={deleteMaster}>Удалить</button>
                </>
              ) : masterFile ? (
                <>
                  <span className="file-name file-new up-ellipsis" title={masterFile.name}>{masterFile.name}</span>
                  <button className="file-replace" onClick={() => setMasterFile(null)}>Отмена</button>
                  <button className="up-primary" onClick={uploadMaster} disabled={masterUploading}>
                    {masterUploading ? 'Загрузка...' : 'Загрузить'}
                  </button>
                </>
              ) : (
                <input type="file" accept=".xlsx" onChange={(e) => setMasterFile(e.target.files?.[0] ?? null)} />
              )}
            </div>
          </div>

          <div className="up-card">
            <h4>Курс валюты</h4>
            <p className="up-card-hint">KKG: сом → тенге.</p>
            <div className="up-inline">
              <input
                type="number"
                step="0.01"
                className="up-search up-rate"
                value={currencyInput}
                onChange={(e) => setCurrencyInput(e.target.value)}
                placeholder="например 5.2"
              />
              <button className="file-replace" onClick={saveCurrencyRate}>Сохранить</button>
            </div>
          </div>
        </div>
        {settingsStatus && <p className="status">{settingsStatus}</p>}
      </section>
    </div>
  );
}
